import { Seo } from "../components/Seo";
import { useEffect, useRef, useState } from "react";
import { useParams, NavLink, useNavigate, useLocation } from "react-router-dom";
import { getJson } from "../lib/http";
import ProductCard from "../components/ProductCard";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { STATIC_PRODUCTS } from "../lib/products";
import { useCategories } from "../lib/categories";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function GroupSidebar({ categories }) {
  const base =
    "block rounded-md border px-3 py-2.5 text-sm font-semibold uppercase tracking-[0.08em] transition-colors";
  const idle =
    "border-white/15 text-[#bfcde3] hover:text-white hover:border-white/40";
  const activeCls = "border-[#20a6da] text-white bg-[#20a6da]/10";
  return (
    <nav
      data-testid="shop-group-sidebar"
      aria-label="Product groups"
      className="space-y-2"
    >
      <NavLink
        to="/shop"
        end
        data-testid="shop-group-all"
        className={({ isActive }) => `${base} ${isActive ? activeCls : idle}`}
      >
        All Products
      </NavLink>
      {categories.map((c) => (
        <NavLink
          key={c.slug}
          to={`/shop/category/${c.slug}`}
          data-testid={`shop-group-${c.slug}`}
          className={({ isActive }) => `${base} ${isActive ? activeCls : idle}`}
        >
          {c.name}
        </NavLink>
      ))}
    </nav>
  );
}

function GroupDropdown({ categories, slug }) {
  const navigate = useNavigate();
  return (
    <div className="lg:hidden mb-4">
      <label className="overline text-[#bfcde3] block mb-1.5" htmlFor="shop-group-select">
        Shop
      </label>
      <div className="relative">
        <select
          id="shop-group-select"
          data-testid="shop-group-dropdown"
          value={slug || ""}
          onChange={(e) =>
            navigate(e.target.value ? `/shop/category/${e.target.value}` : "/shop")
          }
          className="appearance-none w-full rounded-md border border-white/30 bg-black px-3 py-3 pr-10 text-base font-semibold uppercase tracking-[0.08em] text-white focus:outline-none focus:border-[#20a6da]"
        >
          <option value="">All Products</option>
          {categories.map((c) => (
            <option key={c.slug} value={c.slug}>
              {c.name}
            </option>
          ))}
        </select>
        <ChevronDown
          size={18}
          className="group-dropdown-chevron pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white"
        />
        {/* Pulse lives on an overlay div, not the <select> itself —
            iOS Safari won't animate box-shadow on native form controls. */}
        <span
          aria-hidden="true"
          className="group-dropdown-pulse pointer-events-none absolute inset-0 rounded-md"
        />
      </div>
    </div>
  );
}

export default function Shop() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const categories = useCategories();
  const [products, setProducts] = useState(STATIC_PRODUCTS);
  const gridRef = useRef(null);
  const jumpedRef = useRef(false);
  const [slideIdx, setSlideIdx] = useState(0);
  const hasTeasers = categories.length > 0;
  const scrollByOne = (dir) => {
    const el = gridRef.current;
    const first = el?.firstElementChild;
    if (!el || !first) return;
    el.scrollBy({ left: dir * (first.offsetWidth + 24), behavior: "smooth" });
  };
  const onGridScroll = (e) => {
    const el = e.currentTarget;
    const first = el.firstElementChild;
    if (!first) return;
    const step = first.offsetWidth + 24; // slide width + gap-6
    const idx = Math.round(el.scrollLeft / step);
    if (hasTeasers) {
      // Snapped onto the leading teaser → roll back to the previous
      // group, landing on its last product.
      if (idx <= 0) {
        if (!jumpedRef.current) {
          jumpedRef.current = true;
          navigate(prevPath, { state: { jumpToEnd: true } });
        }
        return;
      }
      // Snapped onto the trailing teaser → roll into the next group.
      if (idx >= el.children.length - 1) {
        if (!jumpedRef.current) {
          jumpedRef.current = true;
          navigate(nextPath);
        }
        return;
      }
      setSlideIdx(Math.min(idx - 1, el.children.length - 3));
      return;
    }
    setSlideIdx(Math.min(idx, el.children.length - 1));
  };
  useEffect(() => {
    let active = true;
    // Refetch the catalog on mount, every 15s while the tab is in the
    // foreground, and any time the tab regains visibility so the grid
    // never goes stale on stock.
    const fetchProducts = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      getJson(`${API}/products`)
        .then((data) => {
          if (active && data?.products?.length) {
            setProducts(data.products);
          }
        })
        .catch(() => {});
    };
    fetchProducts();
    const t = setInterval(fetchProducts, 15_000);
    const onVisible = () => {
      if (!document.hidden) fetchProducts();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      active = false;
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const current = slug ? categories.find((c) => c.slug === slug) : null;
  // Every page (groups and All Products) lists cards alphabetically,
  // numeric-aware so 10mg sorts before 20mg.
  const shown = (
    current
      ? products.filter((p) => (p.categories || []).includes(current.slug))
      : products
  )
    .slice()
    .sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    );
  const title = current
    ? `${current.name} | Shop Pepura Labs`
    : "Shop Retatrutide Vials — 10mg to 150mg";
  // Group quick-jump: swiping past the last card rolls into the next
  // group; swiping back past the first card rolls to the end of the
  // previous group (All Products ↔ groups, full circle).
  const groupOrder = [null, ...categories.map((c) => c.slug)];
  const curGroupIdx = groupOrder.indexOf(slug || null);
  const nextSlug = groupOrder[(curGroupIdx + 1) % groupOrder.length];
  const nextPath = nextSlug ? `/shop/category/${nextSlug}` : "/shop";
  const nextLabel = nextSlug
    ? categories.find((c) => c.slug === nextSlug)?.name || "All Products"
    : "All Products";
  const prevSlug =
    groupOrder[(curGroupIdx - 1 + groupOrder.length) % groupOrder.length];
  const prevPath = prevSlug ? `/shop/category/${prevSlug}` : "/shop";
  const prevLabel = prevSlug
    ? categories.find((c) => c.slug === prevSlug)?.name || "All Products"
    : "All Products";
  useEffect(() => {
    // Position the carousel when switching groups: slide 1 (first
    // product, past the leading "previous group" teaser), or the last
    // product when we arrived by swiping back from the next group.
    // Re-runs when async products/categories land so the initial view
    // never sits on a teaser slide.
    jumpedRef.current = false;
    const el = gridRef.current;
    if (!el || !el.firstElementChild) return;
    if (!hasTeasers) {
      el.scrollLeft = 0;
      setSlideIdx(0);
      return;
    }
    const step = el.firstElementChild.offsetWidth + 24;
    const lastProduct = Math.max(el.children.length - 2, 1);
    const target = location.state?.jumpToEnd ? lastProduct : 1;
    el.scrollLeft = step * target;
    setSlideIdx(target - 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, hasTeasers, shown.length]);
  const description = current
    ? current.description ||
      `Browse research-grade ${current.name} products from Pepura Labs Canada.`
    : "Browse research-grade retatrutide vials from 10mg to 150mg plus bacteriostatic water. Lowest prices in Canada, 99%+ purity, Canadian lab COAs, fast shipping.";

  return (
    <div data-testid="shop-page" className="bg-[#0a0a0a]">
      <Seo
        title={title}
        description={description}
        path={current ? `/shop/category/${current.slug}` : "/shop"}
      />
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-10 sm:pt-14">
        <GroupDropdown categories={categories} slug={slug} />
        <p className="hidden lg:block overline text-[#bfcde3]">
          {current ? current.name : "All products"}
        </p>
        {current?.description ? (
          <p className="hidden sm:block mt-2 max-w-2xl text-xl leading-relaxed text-[#bfcde3]">
            {current.description}
          </p>
        ) : null}
      </section>

      <section className="surface-dark">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-6 pb-16 grid gap-8 lg:grid-cols-[230px_1fr]">
          <aside className="hidden lg:block lg:pt-1">
            <GroupSidebar categories={categories} />
          </aside>
          <div className="min-w-0">
            {shown.length === 0 ? (
              <p className="py-16 text-center text-[#bfcde3]">
                No products in this group yet — check back soon.
              </p>
            ) : (
              <div className="relative">
              <div
                ref={gridRef}
                onScroll={onGridScroll}
                data-testid="shop-product-grid"
                className="flex overflow-x-auto snap-x snap-mandatory gap-6 -mx-4 px-4 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:px-0 sm:pb-0 sm:overflow-visible sm:grid sm:grid-cols-2 xl:grid-cols-3"
              >
                {categories.length > 0 && (
                  <button
                    type="button"
                    data-testid="prev-group-slide"
                    onClick={() => navigate(prevPath, { state: { jumpToEnd: true } })}
                    className="sm:hidden w-[calc(100vw-2rem)] shrink-0 snap-center flex flex-col items-center justify-center gap-3 rounded-xl border border-white/20 bg-white/5 min-h-[320px]"
                  >
                    <span className="overline text-[#bfcde3]">Previous</span>
                    <span className="font-display text-2xl uppercase tracking-tight text-white px-6">
                      {prevLabel}
                    </span>
                    <ChevronLeft className="text-[#20a6da]" size={28} />
                  </button>
                )}
                {shown.map((p, i) => (
                  <div
                    key={p.id}
                    className="w-[calc(100vw-2rem)] shrink-0 snap-center sm:w-auto sm:shrink"
                  >
                    <ProductCard product={p} index={i} fromCategory={current?.slug} />
                  </div>
                ))}
                {categories.length > 0 && (
                  <button
                    type="button"
                    data-testid="next-group-slide"
                    onClick={() => navigate(nextPath)}
                    className="sm:hidden w-[calc(100vw-2rem)] shrink-0 snap-center flex flex-col items-center justify-center gap-3 rounded-xl border border-white/20 bg-white/5 min-h-[320px]"
                  >
                    <span className="overline text-[#bfcde3]">Up next</span>
                    <span className="font-display text-2xl uppercase tracking-tight text-white px-6">
                      {nextLabel}
                    </span>
                    <ChevronRight className="text-[#20a6da]" size={28} />
                  </button>
                )}
              </div>
              <button
                type="button"
                aria-label="Previous product"
                data-testid="carousel-prev-btn"
                onClick={() => scrollByOne(-1)}
                className="sm:hidden absolute left-0 top-[38%] -translate-y-1/2 z-10 rounded-full border border-white/20 bg-black/55 backdrop-blur-sm p-2 text-white/80 active:bg-black/80"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                type="button"
                aria-label="Next product"
                data-testid="carousel-next-btn"
                onClick={() => scrollByOne(1)}
                className="sm:hidden absolute right-0 top-[38%] -translate-y-1/2 z-10 rounded-full border border-white/20 bg-black/55 backdrop-blur-sm p-2 text-white/80 active:bg-black/80"
              >
                <ChevronRight size={20} />
              </button>
              </div>
            )}
            {shown.length > 0 && (
              <p
                data-testid="swipe-position-indicator"
                className="sm:hidden mt-2 text-center text-base font-semibold tabular-nums text-[#bfcde3]"
              >
                {Math.min(slideIdx + 1, shown.length)} / {shown.length}
              </p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
